import { useEffect, useState } from "react";
import { RotateCcw, SlidersHorizontal } from "lucide-react";
import { formatInputValue, type DashboardInput } from "@cd/core";
import { fromBoxText, toBoxText } from "../../lib/inputs";
import { cn } from "../../lib/utils";

/**
 * The dashboard's input boxes, one per input id its widgets use. The owner's changes are the saved
 * values everyone sees; anyone else's are theirs alone (the URL's `in`), marked as changed until
 * they reset them.
 */
export function InputsBar({ inputs, tried, canEdit, onSave, onTry }: {
  inputs: DashboardInput[];
  /** Values this viewer is trying instead of the saved ones (the URL's `in`). */
  tried: Record<string, number>;
  canEdit: boolean;
  /** The owner set a new saved value. */
  onSave: (id: string, value: number) => void;
  /** A viewer tried a value, or put it back (undefined). */
  onTry: (id: string, value: number | undefined) => void;
}) {
  if (!inputs.length) return null;
  return (
    <div className="flex basis-full flex-wrap items-start gap-x-4 gap-y-2" role="group" aria-label="Dashboard inputs">
      <SlidersHorizontal className="mt-2 size-3.5 shrink-0 text-subtle" aria-hidden />
      {inputs.map((i) => (
        <InputBox
          key={i.id} input={i} tried={tried[i.id]} canEdit={canEdit}
          onCommit={(v) => (canEdit ? onSave(i.id, v) : onTry(i.id, v === i.saved ? undefined : v))}
          onReset={() => onTry(i.id, undefined)}
        />
      ))}
    </div>
  );
}

function InputBox({ input: i, tried, canEdit, onCommit, onReset }: {
  input: DashboardInput; tried?: number; canEdit: boolean; onCommit: (v: number) => void; onReset: () => void;
}) {
  const value = tried ?? i.saved;
  const [text, setText] = useState(() => toBoxText(i.format, value));
  const [error, setError] = useState<string>();
  // Follow the value when it changes elsewhere (a reset, another tab's save).
  useEffect(() => {
    setText(toBoxText(i.format, value));
    setError(undefined);
  }, [i.format, value]);
  const commit = () => {
    const r = fromBoxText(i.format, text);
    if ("error" in r) return setError(r.error);
    setError(undefined);
    if (r.value !== value) onCommit(r.value);
  };
  const changed = tried !== undefined && tried !== i.saved;
  const id = `input-${i.id}`;
  return (
    <div className="flex flex-col gap-0.5">
      <label htmlFor={id} className="text-[11px] font-medium text-muted">{i.label}</label>
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "flex h-8 items-center rounded-md border bg-surface px-2 text-sm focus-within:ring-2 focus-within:ring-accent/30",
            error ? "border-bad" : changed ? "border-accent" : "border-border",
          )}
        >
          {i.format === "currency" && <span className="text-subtle">$</span>}
          <input
            id={id}
            inputMode="decimal"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setText(toBoxText(i.format, value));
                setError(undefined);
              }
            }}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
            className="w-16 bg-transparent text-right tabular-nums outline-none"
          />
          {i.format === "percent" && <span className="text-subtle">%</span>}
        </div>
        {changed && (
          <span className="flex items-center gap-1.5 text-xs text-accent">
            Changed · saved is {formatInputValue(i.format, i.saved)}
            <button onClick={onReset} className="inline-flex items-center gap-0.5 font-medium hover:underline">
              <RotateCcw className="size-3" /> Reset to saved
            </button>
          </span>
        )}
      </div>
      {error ? (
        <span id={`${id}-error`} className="text-[11px] text-bad">{error}</span>
      ) : (
        !canEdit && !changed && <span className="text-[11px] text-subtle">Try another value; only you see it</span>
      )}
      {canEdit && i.conflicts.length > 0 && (
        <span className="max-w-xs text-[11px] text-warn-fg">
          {i.conflicts.map((t) => `“${t}”`).join(", ")} {i.conflicts.length > 1 ? "have" : "has"} another default for this; this box is used for all of them.
        </span>
      )}
    </div>
  );
}
